import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { parseServerError } from "utils/errors";
import {
  createSandboxApi,
  deleteSandboxApi,
  getSandboxInfoApi,
  switchBackFromSandboxApi,
  switchToSandboxApi,
} from "../api/sandboxApi";
import { normalizeSandboxInfo } from "./sandboxModel";
import { hasSandboxSessionMark, setSandboxSessionMark } from "./sandboxSession";

function rejectMessage(error) {
  return error?.message || "Error";
}

export const fetchSandboxInfo = createAsyncThunk(
  "sandbox/fetchInfo",
  async (_, { rejectWithValue }) => {
    try {
      return normalizeSandboxInfo(await getSandboxInfoApi());
    } catch (error) {
      return rejectWithValue(rejectMessage(error));
    }
  },
);

export const createSandbox = createAsyncThunk(
  "sandbox/create",
  async (payload, { rejectWithValue }) => {
    try {
      return await createSandboxApi(payload);
    } catch (error) {
      return rejectWithValue(rejectMessage(error));
    }
  },
);

export const deleteSandbox = createAsyncThunk(
  "sandbox/delete",
  async (_, { rejectWithValue }) => {
    try {
      return await deleteSandboxApi();
    } catch (error) {
      return rejectWithValue(rejectMessage(error));
    }
  },
);

export const switchToSandbox = createAsyncThunk(
  "sandbox/switch",
  async (_, { rejectWithValue }) => {
    try {
      return await switchToSandboxApi();
    } catch (error) {
      return rejectWithValue(rejectMessage(error));
    }
  },
);

export const switchBackFromSandbox = createAsyncThunk(
  "sandbox/switchBack",
  async (_, { rejectWithValue }) => {
    try {
      return await switchBackFromSandboxApi();
    } catch (error) {
      return rejectWithValue(rejectMessage(error));
    }
  },
);

const initialState = {
  sandboxes: [],
  isSandboxSession: hasSandboxSessionMark(),
  originalCompanyName: null,
  loaded: false,
  fetching: false,
  creating: false,
  deleting: false,
  switching: false,
  error: null,
};

const sandboxSlice = createSlice({
  name: "sandbox",
  initialState,
  reducers: {
    clearSandboxError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSandboxInfo.pending, (state) => {
        state.fetching = true;
        state.error = null;
      })
      .addCase(fetchSandboxInfo.fulfilled, (state, action) => {
        state.fetching = false;
        state.loaded = true;
        state.sandboxes = action.payload.sandboxes;
        state.isSandboxSession = action.payload.isSandboxSession;
        state.originalCompanyName = action.payload.originalCompanyName;
        setSandboxSessionMark(action.payload.isSandboxSession);
      })
      .addCase(fetchSandboxInfo.rejected, (state, action) => {
        state.fetching = false;
        state.loaded = true;
        state.error = parseServerError(action.payload ?? action.error);
      })
      .addCase(createSandbox.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createSandbox.fulfilled, (state) => {
        state.creating = false;
      })
      .addCase(createSandbox.rejected, (state, action) => {
        state.creating = false;
        state.error = parseServerError(action.payload ?? action.error);
      })
      .addCase(deleteSandbox.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteSandbox.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteSandbox.rejected, (state, action) => {
        state.deleting = false;
        state.error = parseServerError(action.payload ?? action.error);
      })
      .addCase(switchToSandbox.pending, (state) => {
        state.switching = true;
        state.error = null;
      })
      .addCase(switchToSandbox.fulfilled, (state) => {
        state.switching = false;
        state.isSandboxSession = true;
        setSandboxSessionMark(true);
      })
      .addCase(switchToSandbox.rejected, (state, action) => {
        state.switching = false;
        state.error = parseServerError(action.payload ?? action.error);
      })
      .addCase(switchBackFromSandbox.pending, (state) => {
        state.switching = true;
        state.error = null;
      })
      .addCase(switchBackFromSandbox.fulfilled, (state) => {
        state.switching = false;
        state.isSandboxSession = false;
        setSandboxSessionMark(false);
      })
      .addCase(switchBackFromSandbox.rejected, (state, action) => {
        state.switching = false;
        state.error = parseServerError(action.payload ?? action.error);
      });
  },
});

export const { clearSandboxError } = sandboxSlice.actions;
export default sandboxSlice.reducer;
